import { stateOptions } from '@/config';
import { validatePrimaryPayer } from '@/utils/validation';

export default {
  fields: [
    {
      label: 'Expected date of dispensing',
      name: 'expected_dispensing_date',
      type: 'datePicker',
      format: 'MM-DD-YYYY',
      placeholder: 'MM-DD-YYYY',
      span: 24,
      required: true,
      style: { width: 'calc(50% - 20px)' },
    },
    {
      label: 'Patient is NOT government insured',
      name: 'non_government_insured',
      type: 'checkbox',
      span: 24,
      extra:
        'Patients are ineligible for copay assistance if they participate in Medicare Part D, Medicaid, Medigap, Veterans Affairs, Department of Defense, Tricare, or any other federal or state-funded programs',
      required: true,
    },
    {
      name: 'non_resident_of_mass',
      label: 'Patient is commercially insured',
      type: 'checkbox',
      span: 12,
      required: true,
    },
    {
      label: 'This fill may be used to treat Primary Humoral Immunodeficiency (PIDD)',
      name: 'treats_cidp',
      type: 'checkbox',
      span: 12,
      required: true,
    },
    {
      label: 'Date of dispensing is not more than 60 days in the past',
      extra:
        'Date of dispensing may be for a future date, today, or sometime within the past 60 days',
      name: 'has_not_dispensed',
      type: 'checkbox',
      span: 24,
      hidden: (meData: any) => !((meData.setting & (4 ^ 8)) > 0),
      required: true,
    },
    {
      label: 'Patient first name',
      name: 'patient_first_name',
      type: 'input',
      span: 12,
      required: true,
    },
    {
      label: 'Patient last name',
      name: 'patient_last_name',
      type: 'input',
      span: 12,
      required: true,
    },
    {
      label: 'Patient gender',
      name: 'patient_gender_code',
      type: 'select',
      span: 12,
      required: true,
      options: [
        { value: 1, label: 'Male' },
        { value: 2, label: 'Female' },
      ],
    },
    {
      label: 'Patient date of birth',
      name: 'date_of_birth',
      type: 'datePicker',
      format: 'MM-DD-YYYY',
      placeholder: 'MM-DD-YYYY',
      dateValidation: {
        disallowFuture: true,
        minAge: 365, // Must be at least 365 days old
        futureMessage: 'Date of birth cannot be in the future',
        minAgeMessage: 'Patient must be at least 1 year old',
      },
      span: 12,
      required: true,
      style: { width: '100%' },
    },
    {
      label: 'Patient address',
      name: 'patient_street_address',
      type: 'input',
      span: 12,
      required: true,
      placeholder: 'Patient street address',
    },
    {
      label: 'Patient address line 2',
      name: 'patient_street_address_line_2',
      type: 'input',
      span: 12,
      placeholder: 'Patient address line 2',
    },
    {
      label: 'Patient city',
      name: 'patient_city_address',
      type: 'input',
      span: 12,
      required: true,
      placeholder: 'City',
    },
    {
      label: 'State',
      name: 'patient_state',
      type: 'select',
      span: 12,
      required: true,
      options: stateOptions,
      placeholder: 'Please select state',
    },
    {
      label: 'Patient zip code',
      name: 'patient_zip',
      type: 'otp',
      span: 8,
      required: true,
      length: 5,
    },
    {
      label: 'Patient phone',
      name: 'patient_phone_number',
      type: 'otp',
      span: 8,
      required: true,
      length: 10,
    },
    {
      label: 'Patient medical record number',
      name: 'patient_medical_record_number',
      type: 'input',
      span: 8,
      required: true,
    },
    {
      label: 'Number of grams for this fill',
      name: 'num_units',
      type: 'inputNumber',
      suffix: 'grams',
      span: 12,
      required: true,
      min: 10,
      max: 300,
    },
    {
      label: 'Number of fills prescribed',
      name: 'num_fills_prescribed',
      type: 'inputNumber',
      span: 12,
      required: true,
      min: 1,
      max: 52,
      placeholder: 'Total number of fills prescribed by the physician',
    },
    {
      label: 'Fill number',
      extra:
        'The first dispensing after the prescription is written is Fill number 0, the following refill is Fill number 1, etc.',
      name: 'fill_num',
      type: 'select',
      span: 12,
      required: true,
      options: [...Array(53).keys()].map((num) => ({ value: num, label: `${num}th` })),
    },
    {
      label: 'Days supply',
      name: 'days_supply',
      type: 'inputNumber',
      span: 12,
      required: true,
      min: 0,
      max: 90,
      placeholder: 'How many days supply is this fill? (e.g. 07 or 28)',
    },
    {
      label: 'Has the patient received XEMBIFY in the past 6 months?',
      name: 'brand_naive',
      type: 'select',
      span: 24,
      allowClear: true,
      options: [
        { value: false, label: 'Has NOT received XEMBIFY in the past 6 months' },
        { value: true, label: 'Has received XEMBIFY in the past 6 months' },
      ],
    },
    {
      label: 'Is the patient new to IG therapy?',
      name: 'drug_class_naive',
      type: 'select',
      span: 24,
      allowClear: true,
      options: [
        { value: false, label: 'Has NOT received IG in the past 6 months' },
        { value: true, label: 'Has received IG in the past 6 months' },
      ],
      placeholder:
        'To the best of your knowledge, has the patient received IG therapy in the past 6 months?',
    },
    {
      label: 'Did the physician specify the brand of IG therapy on the original prescription?',
      name: 'branded_or_not',
      type: 'select',
      span: 24,
      allowClear: true,
      options: [
        { value: true, label: 'Yes, the physician ordered XEMBIFY by name' },
        { value: false, label: 'No, the physician only ordered IG and did not specify a brand' },
      ],
    },
    {
      label: 'Patient diagnosis code',
      name: 'patient_diagnosis_code',
      type: 'select',
      span: 12,
      required: true,
      placeholder: "Please select one of the patient's diagnosis codes from this list",
      optionsFrom: 'icdCodeNames',
    },
    {
      label: 'Date prescription written',
      name: 'date_prescription_written',
      type: 'datePicker',
      format: 'MM-DD-YYYY',
      placeholder: 'MM-DD-YYYY',
      span: 12,
      required: true,
      style: { width: '100%' },
    },
    {
      label: 'Prescribing physician name',
      name: 'prescribing_physician_name',
      type: 'input',
      span: 12,
      required: true,
    },
    {
      label: 'Prescribing physician NPI',
      name: 'prescribing_physician_npi',
      type: 'otp',
      span: 12,
      required: true,
      length: 10,
    },
    {
      label: 'Prescriber specialty',
      name: 'prescribing_physician_specialty',
      type: 'input',
      span: 12,
      required: true,
    },
    {
      label: 'Estimated patient out-of-pocket amount for this fill',
      name: 'estimated_patient_out_of_pocket',
      type: 'input',
      span: 12,
      required: true,
      prefix: '$',
      placeholder: 'e.g. 175.88, 200',
      style: { width: '100%' },
    },
    {
      label: 'Primary payer name',
      name: 'primary_payer',
      type: 'input',
      span: 12,
      required: true,
      placeholder: 'Patient health plan that is covering the medication',
      customValidator: validatePrimaryPayer,
    },
  ],
};
